import { type IntlShape } from '@formatjs/intl'
import {
  type FormatNumberOptions,
  type IntlShape as ReactIntlShape,
} from 'react-intl'

import { i18nGetCurrencyForLocale, i18nGetCurrencySymbol } from '../i18n'

const DEFAULT_FRACTION_DIGITS = 2

const MINUS = '−'

const NON_BREAKING_SPACE = '\u00A0'

/**
 * Price format:
 * ```
 * ┌─ minus sign
 * │ ┌─ currency sign (€) or code (CHF)
 * │ │  ┌─ integer
 * │ │  │ ┌─ decimal (depends on locale)
 * │ │  │ │ ┌─ fraction
 * │ │  │ │ │
 * - € 123,45
 * ```
 *
 * Why `Intl.NumberFormat` doesn't work:
 * - currency position is different across locales
 * - spacing between minus, currency and number is different across locales
 */
export const priceGetFormattedPrice = (
  value: number,
  intl: IntlShape | ReactIntlShape,
  {
    currency = i18nGetCurrencyForLocale(intl.locale),
    maximumFractionDigits = DEFAULT_FRACTION_DIGITS,
    minimumFractionDigits = DEFAULT_FRACTION_DIGITS,
  }: FormatNumberOptions = {},
): string => {
  const absoluteAmount = Math.abs(value)

  const price = intl.formatNumber(absoluteAmount, {
    maximumFractionDigits,
    minimumFractionDigits,
  })

  const currencySymbol = i18nGetCurrencySymbol(currency, intl)

  const pricesParts = [currencySymbol, price]

  if (value < 0) {
    pricesParts.unshift(MINUS)
  }

  return pricesParts.join(NON_BREAKING_SPACE)
}
